import {
  ConnectedWallet,
  InputType,
  KindOfKit,
  Kit,
  KitFunction,
  KitFunctionMethod,
  MethodInput,
  ResourceRegistry,
} from '../../../../assets/constant/kit.type';
import { TOKEN_KIT_FUNCTION_METHOD } from '../../../../assets/constant/tokenKit.constant';
import React, { useEffect, useMemo, useState } from 'react';
import KitContainer from '../KitContainer';
import DivideLine from '../../../../components/_atom/line/DivideLine';
import toast from 'react-hot-toast';
import '../LabelValue.scss';
import EnterPinCodeModal from '../EnterPinCodeModal';
import OwnerBadge from '../OwnerBadge';
import iconWarningGrey from '../../../../assets/images/atom/Icon_warning_grey.png';
import { NFT_KIT_FUNCTION_METHODS } from '../../../../assets/constant/nftKit.constant';
import { logic } from '../../../../../logic';
import MethodForm, { MethodFormProps } from './MethodForm';
import './MethodContainer.scss';
import BatchMethodForm from './BatchMethodForm';
import BasicButton from '../../../../components/_atom/button/BasicButton';
import { ButtonSize, ButtonTheme } from '../../../../components/_atom/button/StyledButton';
import leftArrow from '../../../../assets/images/arrow_left_btn_large.png';

const MethodContainer = ({
  method,
  connectedWallet,
  selectedFunction,
  kitDetail,
  hasTXWebhook,
  kitType,
}: {
  method: KitFunctionMethod;
  connectedWallet: ConnectedWallet;
  selectedFunction?: KitFunction;
  kitDetail: Kit;
  hasTXWebhook: boolean;
  kitType: KindOfKit;
}) => {
  const { method_id, input_type, method_description, method_name, inputs, owner_only, type } = method;
  const kitFunctionMethod = kitType === KindOfKit.TokenKit ? TOKEN_KIT_FUNCTION_METHOD : NFT_KIT_FUNCTION_METHODS;
  const selectedFunctionMethods = kitFunctionMethod[selectedFunction?.feature_id ?? 1];
  const currentMethod = selectedFunctionMethods.find((item) => item.method_id === method_id);
  const [selectedType, setSelectedType] = useState<InputType>();
  useEffect(() => {
    setSelectedType(undefined);
  }, [method]);
  const [currentInput, setCurrentInput] = useState<MethodInput>();
  const [inputValue, setInputValue] = useState<any>({});
  const [multipleInputValues, setMultipleInputValues] = useState<any[]>([]);
  useEffect(() => {
    if (selectedType) {
      const input = inputs.find((item) => item.input_id === selectedType.input_id);
      setCurrentInput(input);
      return;
    }
    setCurrentInput(inputs[0]);
  }, [selectedType, inputs]);

  useEffect(() => {
    setInputValue({});
    setMultipleInputValues([]);
  }, [method]);

  const valueKeyHasMetadata = useMemo(() => {
    const totalInputs = [...(currentInput?.required ?? []), ...(currentInput?.optional ?? [])];
    const metadataInputs = totalInputs?.filter((item) => item.is_metadata);
    return metadataInputs?.[0]?.metadata_key;
  }, [currentInput]);

  const [result, setResult] = useState<any>();
  useEffect(() => {
    setResult(undefined);
  }, [selectedFunction, method]);

  const [selectedMethodType, setSelectedMethodType] = useState<'single' | 'multiple'>('single');

  const [isOpenEnterPinCodeModal, setIsOpenEnterPinCodeModal] = useState(false);
  const [password, setPassword] = useState<string>('');
  const [useWebhook, setUseWebhook] = useState<boolean>(false);
  const [webhookUrl, setWebhookUrl] = useState<string>('');

  const [loading, setLoading] = useState(false);
  const [resourceRegistry, setResourceRegistry] = useState<ResourceRegistry>();
  const getResourceRegistry = async () => {
    const { result, error } = await logic.metadataManager.getResourceRegistryList({
      categoryIdentifier: kitDetail?.category_identifier,
      resourceIdentifier: kitDetail?.kit_id,
    });
    if (error) {
      console.error(error);
      return;
    }
    const { rows } = result;
    setResourceRegistry(rows[0]);
  };
  useEffect(() => {
    getResourceRegistry();
  }, [kitDetail]);

  const uploadMetadata = async (metadata: any, tokenId: string) => {
    const metadataInputKeys = Object.keys(metadata);
    if (metadataInputKeys.length > 0) {
      metadataInputKeys.map((metadataInputKey) => {
        if (metadata[metadataInputKey] instanceof File) {
          metadata.metadata_key = metadataInputKey;
        }
        return metadata;
      });
    }
    if (kitDetail?.nft_kit_id && resourceRegistry && metadata) {
      const { result, error } = await logic.nftKit.createMetadataUrlWithS3(
        tokenId,
        kitDetail.nft_kit_id,
        kitDetail.category_identifier,
        resourceRegistry.service_identifier,
        {
          tokenMetadata: metadata,
        }
      );
      if (error) {
        console.error(error);
        return { result: undefined, error };
      }
      return { result, error: undefined };
    }
    return { result: undefined, error: 'invalid request' };
  };
  const runMethodSingle = async (inputValue: any) => {
    setLoading(true);
    toast.loading('Processing...', { duration: 1500 });
    if (inputValue?.metadata) {
      let parsedInputValue = inputValue;
      const { result: nextTokenId, error: nextTokenIdError } = await logic.nftKit.callChainForTokenId(kitDetail?.chain_id, kitDetail?.contract_address);
      if (nextTokenIdError) {
        toast.error('Get token id failed');
        return;
      }
      const { result: metadataUploadResult, error: metadataUploadError } = await uploadMetadata(inputValue.metadata, nextTokenId);
      if (metadataUploadError) {
        toast.error('Metadata upload failed');
        return;
      }
      const metadata_uri = metadataUploadResult ? metadataUploadResult[valueKeyHasMetadata || 'token_uri'] : undefined;
      delete parsedInputValue.metadata;
      parsedInputValue[valueKeyHasMetadata || 'token_uri'] = metadata_uri;
      const inputValues = input_type === 'multiple' ? [parsedInputValue] : parsedInputValue;
      const result = await currentMethod.onClick(inputValues, {
        kitId: kitDetail?.kit_id || kitDetail?.nft_kit_id,
        microChainId: kitDetail?.chain_id,
        contractAddress: kitDetail?.contract_address,
        connectedWallet,
        password,
        useWebhook,
        webhookUrl,
      });
      if (result) {
        setResult(result);
        setMultipleInputValues([]);
        toast.success('Method executed successfully!');
      }
    } else {
      const inputValues = input_type === 'multiple' ? [inputValue] : inputValue;
      const result = await currentMethod.onClick(inputValues, {
        kitId: kitDetail?.kit_id || kitDetail?.nft_kit_id,
        microChainId: kitDetail?.chain_id,
        contractAddress: kitDetail?.contract_address,
        connectedWallet,
        password,
        useWebhook,
        webhookUrl,
      });
      if (result) {
        setResult(result);
        setMultipleInputValues([]);
        toast.success('Method executed successfully!');
      }
    }
  };
  const runMethodMultiple = async (multipleInputValues) => {
    setLoading(true);
    toast.loading('Processing...');
    let nextTokenId;
    let nextTokenIdError;
    if (kitDetail?.category_identifier !== 3 && method_id === 101) {
      const { result, error } = await logic.nftKit.callChainForTokenId(kitDetail?.chain_id, kitDetail?.contract_address);
      nextTokenId = Number(result);
      nextTokenIdError = error;
    }
    if (nextTokenIdError) {
      toast.error('Get token id failed');
      return;
    }
    try {
      const inputValues = await Promise.all(
        multipleInputValues.map(async (inputValue, index) => {
          if (inputValue?.metadata) {
            const { result: metadataUploadResult, error: metadataUploadError } = await uploadMetadata(inputValue.metadata, nextTokenId + index);
            if (metadataUploadError) {
              throw metadataUploadError;
            }
            const metadata_uri = metadataUploadResult ? metadataUploadResult[valueKeyHasMetadata || 'token_uri'] : undefined;
            if (!metadata_uri) {
              return inputValue;
            }
            return {
              ...inputValue,
              [valueKeyHasMetadata || 'token_uri']: metadata_uri,
            };
          }
          return inputValue;
        })
      );
      const reversedInputValues = inputValues.reverse();
      const result = await currentMethod.onClick(reversedInputValues, {
        kitId: kitDetail?.kit_id || kitDetail?.nft_kit_id,
        microChainId: kitDetail?.chain_id,
        contractAddress: kitDetail?.contract_address,
        connectedWallet,
        password,
        useWebhook,
        webhookUrl,
      });
      if (result) {
        setResult(result);
        setMultipleInputValues([]);
        toast.success('Method executed successfully!');
      } else {
        toast.dismiss();
      }
    } catch (e) {
      toast.error('Metadata upload failed');
      return;
    }
  };
  const runMethod = async (method?: 'multiple' | 'single') => {
    if (method) {
      if (method === 'single') {
        return runMethodSingle(inputValue);
      } else if (method === 'multiple') {
        return runMethodMultiple(multipleInputValues);
      }
    } else {
      if (selectedMethodType === 'single') {
        return runMethodSingle(inputValue);
      }
      if (selectedMethodType === 'multiple') {
        return runMethodMultiple(multipleInputValues);
      }
    }
  };

  return (
    <KitContainer
      title={
        <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
          {method_name}
          {owner_only && <OwnerBadge />}
        </div>
      }
      description={method_description}
      warning={
        owner_only && (
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <img src={iconWarningGrey} alt={'!'} style={{ width: 24, height: 24, objectFit: 'contain' }} />
            <div style={{ fontWeight: 400, fontSize: 14, lineHeight: '24px', color: '#7F879E' }}>
              This function in kit can only be executed by the kit owner account
            </div>
          </div>
        )
      }
      Button={
        type &&
        selectedType && (
          <BasicButton size={ButtonSize.DYNAMIC} theme={ButtonTheme.NORMAL} onClick={() => setSelectedType(undefined)}>
            <img src={leftArrow} alt="<" style={{ width: 16, height: 16, objectFit: 'contain', gap: 5 }} />
            Back to choose another method
          </BasicButton>
        )
      }
    >
      <EnterPinCodeModal
        isOpen={isOpenEnterPinCodeModal}
        closeModal={async () => {
          setIsOpenEnterPinCodeModal(false);
          if (password.length === 6) {
            runMethod().finally(() => {
              setPassword('');
              setLoading(false);
            });
          }
        }}
        connectedWallet={connectedWallet}
        pin={password}
        setPin={setPassword}
        title={'Enter your Kit PIN'}
      />
      <DivideLine />
      {type ? (
        <TypeSelector
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          types={type}
          method={method}
          hasTXWebhook={hasTXWebhook}
          kitDetail={kitDetail}
          connectedWallet={connectedWallet}
          currentInput={currentInput}
          inputValue={inputValue}
          setInputValue={setInputValue}
          multipleInputValues={multipleInputValues}
          setMultipleInputValues={setMultipleInputValues}
          result={result}
          setSelectedMethodType={setSelectedMethodType}
          setIsOpenEnterPinCodeModal={setIsOpenEnterPinCodeModal}
          password={password}
          setPassword={setPassword}
          useWebhook={useWebhook}
          setUseWebhook={setUseWebhook}
          webhookUrl={webhookUrl}
          setWebhookUrl={setWebhookUrl}
          loading={loading}
          setLoading={setLoading}
          runMethod={runMethod}
          resourceRegistry={resourceRegistry}
        />
      ) : (
        <MethodForm
          method={method}
          hasTXWebhook={hasTXWebhook}
          kitDetail={kitDetail}
          connectedWallet={connectedWallet}
          currentInput={currentInput}
          inputValue={inputValue}
          setInputValue={setInputValue}
          multipleInputValues={multipleInputValues}
          setMultipleInputValues={setMultipleInputValues}
          result={result}
          setSelectedMethodType={setSelectedMethodType}
          setIsOpenEnterPinCodeModal={setIsOpenEnterPinCodeModal}
          password={password}
          setPassword={setPassword}
          useWebhook={useWebhook}
          setUseWebhook={setUseWebhook}
          webhookUrl={webhookUrl}
          setWebhookUrl={setWebhookUrl}
          loading={loading}
          setLoading={setLoading}
          runMethod={runMethod}
        />
      )}
    </KitContainer>
  );
};

export default MethodContainer;

type TypeSelectorProps = {
  selectedType: InputType | undefined;
  setSelectedType: (type: InputType) => void;
  types?: InputType[];
  resourceRegistry?: ResourceRegistry;
} & MethodFormProps;
const TypeSelector = (props: TypeSelectorProps) => {
  const { selectedType, setSelectedType, types, connectedWallet, kitDetail, resourceRegistry, ...rest } = props;
  const isKitOwner = kitDetail?.admin_address?.toLowerCase() === connectedWallet.connectAddress?.toLowerCase();
  const disabled = !isKitOwner || !connectedWallet.connectAddress;
  return (
    <div className="method-type-container-wrapper">
      {!selectedType ? (
        types?.map((type) => (
          <div className={`method-type-container${disabled ? ' disabled' : ''}`} key={type.type_id} onClick={() => setSelectedType(type)}>
            <div className="method-type-image-wrapper">
              <img src={disabled ? type.type_img_disabled : type.type_img} alt={''} className="method-type-image" />
            </div>
            <div className="method-type-name">{type.type_name}</div>
            <div className="method-type-description">{type.type_description}</div>
          </div>
        ))
      ) : (
        <>
          {selectedType.type_id === 1 ? (
            <MethodForm connectedWallet={connectedWallet} kitDetail={kitDetail} {...rest} />
          ) : (
            <BatchMethodForm connectedWallet={connectedWallet} kitDetail={kitDetail} resourceRegistry={resourceRegistry} {...rest} />
          )}
        </>
      )}
    </div>
  );
};
